import { useClickOutside } from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@workspace/ui/components/Button';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { company } from '~/company/Company';
import Input from '~/components/isolated/Input';
import { LocateMeButton } from '~/components/LocateMeButton';
import Modal from '~/components/Modal/Modal';
import { useGlobal } from '~/hooks/useGlobal';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { useSaveAddress } from '~/hooks/useSaveAddress';
import { useSetMapCenter } from '~/hooks/useSetMapCenter';
import { mainStore, orderStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import { ModalFooter } from '../Modal/components/ModalFooter';
import { ModalHeader } from '../Modal/components/ModalHeader';
import { ModalType } from '../Modal/interface';
import { ModalBody } from '../ModalComponents';
import { REDIRECT_QUERY_NAME } from '../ModalDeliveryAddress/constants';
import { deliveryAddressStore } from '../ModalDeliveryAddress/DeliveryAddressStore';
import Geocoding from '../ModalDeliveryAddress/Geocoding/Geocoding';
import GMap from '../ModalDeliveryAddress/GMap/GMap';
import geocoderRequest from '../ModalDeliveryAddress/geocoderRequest';
import {
  saveAuthorizedAddress,
  saveUnauthorizedAddress,
  sendAddressChangedAnalytics,
  sendPostSaveAnalytics,
  updateEtaAndDeliveryMethod,
} from '../ModalDeliveryAddress/saveAddressUtils';
import SaveButton from '../ModalEditAddressDetailsV3/SaveButton';

import styles from './ModalEditAddressLocationV3.module.scss';

interface AutocompleteResponse {
  data: {
    result: {
      address: string;
      postcode: null | string;
      location: {
        latitude: number;
        longitude: number;
      };
    } | null;
  };
}

const ModalEditAddressLocation = observer(() => {
  const { t } = useTranslation();

  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
  const [addressInputText, setAddressInputText] = useState('');
  const [autocompletePlaceId, setAutocompletePlaceId] = useState<string>();
  const [isPudoPopupOpen, setIsPudoPopupOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const editableAddressId = searchParams.get(MODAL_EDIT_ADDRESS_PARAM);
  const editableAddress = userStore.isAuthorized
    ? null
    : userStore.deliveryAddress?.address1;

  const addressFormRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useGlobal();

  const { closeModal, openModal, goBack } = useModal();
  useSetMapCenter(userStore.deliveryAddress);

  useEffect(() => {
    if (isAutoCompleteOpen) {
      addressInputRef.current?.focus();
    }
  }, [isAutoCompleteOpen]);

  useEffect(() => {
    userStore.resetAddressSearchSessionToken();
  }, []);

  useEffect(() => {
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  }, [deliveryAddressStore.inputAddressValue]);

  useClickOutside(addressFormRef, () => {
    setIsAutoCompleteOpen(false);
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  });

  useEffect(() => {
    return () => {
      mainStore.alerts.pop();
      deliveryAddressStore.resetStore();
    };
  }, []);

  useEffect(() => {
    setAutocompletePlaceId(undefined);
  }, [deliveryAddressStore.deliveryAddress?.placeId]);

  useEffect(() => {
    if (editableAddressId) {
      const editableAddress = userStore.addressList.find(
        (item) => item.id === editableAddressId,
      );

      if (editableAddress) {
        const point = {
          lat: editableAddress.latitude,
          lng: editableAddress.longitude,
        };

        deliveryAddressStore.setEditAddressData({
          addressId: editableAddress.id,
          address2: editableAddress.street_address_2 ?? '',
          addressFloor: editableAddress.floor ?? '',
          addressBld: editableAddress.building ?? '',
          addressApt: editableAddress.apartment ?? '',
          type: editableAddress.type ?? 'home',
          comment: editableAddress.comment ?? '',
          instructions: editableAddress.instructions ?? [],
        });

        deliveryAddressStore.selectZoneByCoordinates(point);
      }
    }
  }, [editableAddressId]);

  const {
    data: { result: autocompleteResult },
    isFetching,
  } = useQuery({
    enabled: !!userStore.addressSearchSessionToken && !!autocompletePlaceId,
    queryKey: ['autocomplete-get-place', autocompletePlaceId],
    initialData: {
      result: null,
    },
    queryFn: () =>
      geocoderRequest
        .post<AutocompleteResponse>('/autocomplete', {
          input: '',
          placeId: autocompletePlaceId,
          sessionToken: userStore.addressSearchSessionToken,
          countryCode: company.config.countryCode || '',
        })
        .then(({ data: { data } }) => data),
  });

  useEffect(() => {
    if (!autocompleteResult) {
      return;
    }

    const point = {
      lat: autocompleteResult.location.latitude,
      lng: autocompleteResult.location.longitude,
    };

    deliveryAddressStore.selectZoneByCoordinates(point);

    deliveryAddressStore.setDeliveryAddress({
      zip: '',
      country: '',
      city: '',
      region: '',
      address1: '',
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      shortAddress: '',
      coordinates: point,
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      type: 'home',
    });

    if (deliveryAddressStore.selectedZone) {
      deliveryAddressStore.geocoding(false, {
        lng: autocompleteResult.location.longitude,
        lat: autocompleteResult.location.latitude,
      });
    } else {
      orderStore.requestETA();
    }
  }, [autocompleteResult, deliveryAddressStore.renderedZonesOnMap]);

  useEffect(() => {
    deliveryAddressStore.setIsLoading(isFetching);
  }, [isFetching]);

  const setAutocompleteActive = () => {
    setIsAutoCompleteOpen(true);
  };

  const handleCloseModal = () => {
    mainStore.alerts.pop();
    closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handlePudoSelect = async (center: { lat: number; lng: number }) => {
    deliveryAddressStore.setIsLoading(true);
    try {
      const selectedZone = deliveryAddressStore.getSelectedPolygon(center);
      deliveryAddressStore.setSelectedZone(selectedZone);

      await deliveryAddressStore.geocoding(false, center);

      const deliveryAddress = deliveryAddressStore.deliveryAddress;
      if (!deliveryAddress?.city || !deliveryAddressStore.etaCalculation) {
        return;
      }

      const editAddressData = deliveryAddressStore.editAddressData;

      sendAddressChangedAnalytics(
        deliveryAddress,
        deliveryAddressStore.etaCalculation,
      );

      if (userStore.isAuthorized) {
        await saveAuthorizedAddress(deliveryAddress, editAddressData, {
          alwaysSetDeliveryAddress: true,
        });
      } else {
        saveUnauthorizedAddress(deliveryAddress, editAddressData);
      }

      await updateEtaAndDeliveryMethod(false);
      sendPostSaveAnalytics(deliveryAddress);
      handleCloseModal();
    } finally {
      deliveryAddressStore.setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    goBack([MODAL_EDIT_ADDRESS_PARAM]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handleOpenModal = (addressId: string) => {
    if (editableAddressId) {
      goBack();
    } else if (addressId) {
      openModal(
        ModalType.EditAddressDetails,
        ModalType.EditAddressLocation,
        {
          [MODAL_EDIT_ADDRESS_PARAM]: addressId,
          [REDIRECT_QUERY_NAME]: redirectNext || '',
        },
        false,
        true,
      );
    } else {
      closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
    }
  };

  const { handleSaveAddress, isLoading, isDisabled } = useSaveAddress({
    shouldValidateBuilding: true,
    onClick: handleOpenModal, // ваш handleOpenModal
  });

  const getFormattedType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const handleChangeLocationName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && value.trim().toLowerCase() === 'home') {
      deliveryAddressStore.setAddressType('home');
    } else {
      deliveryAddressStore.setAddressTypeValue(value);
    }
  };

  const autocompleteInput = (
    <div
      ref={addressFormRef}
      className={classes([
        styles.autocompleteInputContainer,
        isAutoCompleteOpen && styles.addressInputContainerFocused,
        isAutoCompleteOpen && 'mobile__opened',
      ])}
    >
      <Input
        size="M"
        rounded
        data-input="address"
        placeholder={
          addressInputText
            ? t(isAutoCompleteOpen ? 'typeLocation' : 'addressNotSet')
            : editableAddress || t('addressNotSet')
        }
        value={addressInputText}
        onChange={(e) => setAddressInputText(e.target.value)}
        onFocus={() => setAutocompleteActive()}
        readOnly
        disabled={!addressInputText}
        ref={addressInputRef}
      />
    </div>
  );

  const title = userStore.isAuthorized
    ? editableAddressId
      ? t('editAddress')
      : t('checkout:addNewAddress')
    : t('setDeliveryAddress');

  return (
    <Modal
      isShow
      className={styles.selectLocation}
      onBackdropDismiss={handleCloseModal}
    >
      <div className={styles.selectLocation}>
        <div className={styles.selectLocation__container}>
          <div className={styles.modalContainer}>
            <ModalHeader
              variant={company.match({
                mcosmetic: 'default',
                default: isMobile ? 'dark' : 'default',
              })}
              onClose={isMobile ? undefined : handleCloseModal}
              title={title}
            />
            {!isMobile && company.showAddressSubtitle && (
              <p className={styles.subtitle}>
                {t('checkout:addNewAddressSubtitle')}
              </p>
            )}
          </div>
          <ModalBody className={styles.selectLocation__container__body}>
            {isMobile && userStore.isAuthorized ? (
              <div className={styles.inputContainer}>
                <Input
                  size="S"
                  rounded
                  variant="transparent"
                  label={t('checkout:locationName')}
                  data-input="address-custom-name"
                  placeholder={t('addressCustomName')}
                  defaultValue={
                    deliveryAddressStore.editAddressData.type?.toLowerCase() ===
                    'home'
                      ? getFormattedType(
                          deliveryAddressStore.editAddressData.type,
                        )
                      : ''
                  }
                  value={getFormattedType(
                    deliveryAddressStore.editAddressData.type,
                  )}
                  onChange={handleChangeLocationName}
                />
              </div>
            ) : null}

            <div
              className={classes([
                styles.mapContainer,
                !userStore.isAuthorized && styles.notAuthorized,
              ])}
            >
              <Geocoding />
              <GMap
                onPudoSelect={handlePudoSelect}
                onPudoPopupChange={setIsPudoPopupOpen}
              />
              {deliveryAddressStore.isShowOutOfZoneInline && (
                <div className={styles.outOfZoneContainer}>
                  <div className={styles.outOfZoneContent}>
                    <p className={styles.outOfZoneText}>
                      {t('outOfZone:notAvailableYet')}
                    </p>
                    <Button
                      variant="outlined"
                      size="xl"
                      onClick={() => {
                        deliveryAddressStore.setIsOutOfZoneModalOpen(true);
                      }}
                      endIcon={<Icon type="notification" size={20} />}
                    >
                      {t('outOfZone:notifyMe')}
                    </Button>
                  </div>
                  <Icon type="pointer" size={16} className={styles.pointer} />
                </div>
              )}
              {!isPudoPopupOpen && !company.isPudoOnly && (
                <>
                  {(isMobile || !addressInputText) &&
                    !deliveryAddressStore.isShowOutOfZoneInline && (
                      <div className={styles.address}>
                        <div>
                          {addressInputText ? addressInputText : t('movePin')}
                        </div>
                        <Icon
                          type="pointer"
                          size={16}
                          className={styles.pointer}
                        />
                      </div>
                    )}
                  <Icon
                    type="pinV3"
                    size={company.match({
                      citydrinks: isMobile ? 44 : 64,
                      v3: isMobile ? 44 : 64,
                      default: 64,
                    })}
                    className={styles.mapPin}
                  />
                </>
              )}
              {isMobile && !deliveryAddressStore.isShowOutOfZoneInline && (
                <LocateMeButton
                  hasText={!addressInputText}
                  className="absolute bottom-4 left-4 right-4 w-auto"
                />
              )}
            </div>
            {isMobile && company.showAddressSubtitle && (
              <p className={styles.mobileSubtitle}>
                {t('checkout:addNewAddressSubtitle')}
              </p>
            )}
          </ModalBody>

          {isMobile ? (
            <ModalFooter
              onBack={handleGoBack}
              primaryAction={{
                text: t('checkout:confirm'),
                onClick: handleSaveAddress,
                disabled: isDisabled,
                isLoading,
              }}
            />
          ) : (
            <div className={styles.selectLocation__container__footer}>
              <div className={styles.modalForm}>{autocompleteInput}</div>
              <LocateMeButton />
              <SaveButton
                shouldValidateBuilding
                className={styles.proceedButton}
                title={t('continue')}
                onClick={handleOpenModal}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default ModalEditAddressLocation;
